import { Node, Edge, MarkerType } from "reactflow";
import { DiagramData } from "@/components/diagrams/DiagramViewer";
import { supabase } from "@/lib/supabaseClient";

// Hàm tạo ID duy nhất cho edge
const createEdgeId = (source: string, target: string): string =>
  `${source}-${target}`;

// Cache cho category names để tránh gọi API nhiều lần
const categoryCache: Record<string, string> = {};

// Hàm lấy tên danh mục từ category_id
export const getCategoryName = async (categoryId: string): Promise<string> => {
  // Nếu đã có trong cache, trả về từ cache
  if (categoryCache[categoryId]) {
    return categoryCache[categoryId];
  }

  try {
    // Lấy thông tin danh mục từ database
    const { data, error } = await supabase
      .from("categories")
      .select("name")
      .eq("id", categoryId)
      .single();

    if (error || !data) {
      console.error("Error fetching category name:", error);
      return ""; // Trả về chuỗi rỗng nếu có lỗi
    }

    // Lưu vào cache
    categoryCache[categoryId] = data.name;

    return data.name;
  } catch (error) {
    console.error("Error in getCategoryName:", error);
    return ""; // Trả về chuỗi rỗng nếu có lỗi
  }
};

// Đảm bảo tất cả các node và children có category_name
const prepareDiagramData = (data: DiagramData): DiagramData => {
  // Đảm bảo node gốc có category_name
  const rootCategory = data.category_name || null;

  // Hàm đệ quy để cập nhật category_name cho tất cả các node
  const updateNodeCategories = (node: DiagramData): DiagramData => {
    // Nếu node không có category_name nhưng có category_id, sử dụng category_name của node cha hoặc giữ nguyên category_id
    if (!node.category_name && node.category_id) {
      // Nếu node có cùng category_id với node cha, sử dụng category_name của node cha
      if (data.category_id === node.category_id && rootCategory) {
        node.category_name = rootCategory;
      }
    }

    // Cập nhật đệ quy cho tất cả children
    if (node.children && node.children.length > 0) {
      node.children = node.children.map((child) => {
        // Đảm bảo child có category_name nếu có cùng category_id với node cha
        if (
          !child.category_name &&
          child.category_id === node.category_id &&
          node.category_name
        ) {
          child.category_name = node.category_name;
        }
        // Xử lý đệ quy các con của child
        return updateNodeCategories(child);
      });
    }

    return node;
  };

  // Cập nhật category_name cho tất cả các node
  return updateNodeCategories(data);
};

// Chuyển đổi dữ liệu sang định dạng Mindmap
export const transformToMindmap = (
  data: DiagramData,
): { nodes: Node[]; edges: Edge[] } => {
  // Đảm bảo dữ liệu diagram đã có đầy đủ category_name
  const processedData = prepareDiagramData(data);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Hàm đệ quy để xử lý node và con của nó
  const processNode = (
    node: DiagramData,
    level: number = 0,
    parentX: number = 0,
    parentY: number = 0,
    angle: number = 0,
    parentId: string | null = null,
  ) => {
    // Tính toán vị trí dựa trên cấp độ và góc
    const distance = 300;
    const x = parentId ? parentX + Math.cos(angle) * distance : 0;
    const y = parentId ? parentY + Math.sin(angle) * distance : 0;

    // Thêm node vào danh sách
    nodes.push({
      id: node.id,
      type: "diagramNode",
      data: {
        label: node.name,
        description: node.description,
        image: node.image,
        showImage: node.showImage,
        type: node.id.split("-").pop() || "",
        isOval: true,
        // Sử dụng category_name nếu có, nếu không thì sử dụng category_id
        category: node.category_id,
        category_name: node.category_name,
      },
      position: { x, y },
    });

    // Thêm edge nếu có parent
    if (parentId) {
      // Xác định màu sắc: highlight (đỏ) hoặc bình thường (xám)
      const isHighlighted = node.highlight === true;
      const edgeColor = isHighlighted ? "#ef4444" : "#64748b";

      edges.push({
        id: createEdgeId(parentId, node.id),
        source: parentId,
        target: node.id,
        type: "default",
        animated: false,
        style: { strokeWidth: 2, stroke: edgeColor },
      });
    }

    // Xử lý các node con
    if (node.children && node.children.length > 0) {
      const angleStep = (2 * Math.PI) / node.children.length;
      node.children.forEach((child, index) => {
        const childAngle = angle + angleStep * index;
        processNode(child, level + 1, x, y, childAngle, node.id);
      });
    }
  };

  // Bắt đầu từ node gốc
  processNode(processedData);

  return { nodes, edges };
};

// Chuyển đổi dữ liệu sang định dạng Tree (từ trên xuống dưới)
export const transformToTree = (
  data: DiagramData,
): { nodes: Node[]; edges: Edge[] } => {
  // Đảm bảo dữ liệu diagram đã có đầy đủ category_name
  const processedData = prepareDiagramData(data);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Hàm đệ quy để xử lý node và con của nó
  const processNode = (
    node: DiagramData,
    level: number = 0,
    position: number = 0,
    parentId: string | null = null,
  ) => {
    // Tính toán vị trí dựa trên cấp độ và vị trí
    const x = position * 350;
    const y = level * 200;

    // Thêm node vào danh sách
    nodes.push({
      id: node.id,
      type: "diagramNode",
      data: {
        label: node.name,
        description: node.description,
        image: node.image,
        showImage: node.showImage,
        type: node.id.split("-").pop() || "",
        isOval: true,
        // Sử dụng category_name nếu có, nếu không thì sử dụng category_id
        category: node.category_id,
        category_name: node.category_name,
      },
      position: { x, y },
    });

    // Thêm edge nếu có parent
    if (parentId) {
      // Xác định màu sắc: highlight (đỏ) hoặc bình thường (xám)
      const isHighlighted = node.highlight === true;
      const edgeColor = isHighlighted ? "#ef4444" : "#64748b";

      edges.push({
        id: createEdgeId(parentId, node.id),
        source: parentId,
        target: node.id,
        type: "default",
        animated: false,
        style: { strokeWidth: 2, stroke: edgeColor },
      });
    }

    // Xử lý các node con
    if (node.children && node.children.length > 0) {
      // Tính toán vị trí bắt đầu cho các node con
      const childrenCount = node.children.length;
      const startPosition = position - (childrenCount - 1) / 2;

      node.children.forEach((child, index) => {
        const childPosition = startPosition + index;
        processNode(child, level + 1, childPosition, node.id);
      });
    }
  };

  // Bắt đầu từ node gốc
  processNode(processedData);

  return { nodes, edges };
};

// Chuyển đổi dữ liệu sang định dạng Radial
export const transformToRadial = (
  data: DiagramData,
): { nodes: Node[]; edges: Edge[] } => {
  // Đảm bảo dữ liệu diagram đã có đầy đủ category_name
  const processedData = prepareDiagramData(data);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Hàm đệ quy để xử lý node và con của nó
  const processNode = (
    node: DiagramData,
    level: number = 0,
    angle: number = 0,
    parentId: string | null = null,
  ) => {
    // Tính toán vị trí dựa trên cấp độ và góc
    const radius = level * 300;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;

    // Thêm node vào danh sách
    nodes.push({
      id: node.id,
      type: "diagramNode",
      data: {
        label: node.name,
        description: node.description,
        image: node.image,
        showImage: node.showImage,
        type: node.id.split("-").pop() || "",
        isOval: true,
        // Sử dụng category_name nếu có, nếu không thì sử dụng category_id
        category: node.category_id,
        category_name: node.category_name,
      },
      position: { x, y },
    });

    // Thêm edge nếu có parent
    if (parentId) {
      // Xác định màu sắc: highlight (đỏ) hoặc bình thường (xám)
      const isHighlighted = node.highlight === true;
      const edgeColor = isHighlighted ? "#ef4444" : "#64748b";

      edges.push({
        id: createEdgeId(parentId, node.id),
        source: parentId,
        target: node.id,
        type: "default",
        animated: false,
        style: { strokeWidth: 2, stroke: edgeColor },
      });
    }

    // Xử lý các node con
    if (node.children && node.children.length > 0) {
      const angleStep = (2 * Math.PI) / node.children.length;
      node.children.forEach((child, index) => {
        const childAngle = angle + angleStep * index;
        processNode(child, level + 1, childAngle, node.id);
      });
    }
  };

  // Bắt đầu từ node gốc
  processNode(processedData);

  return { nodes, edges };
};

// Chuyển đổi dữ liệu sang định dạng Horizontal Flow
export const transformToHorizontal = (
  data: DiagramData,
): { nodes: Node[]; edges: Edge[] } => {
  // Đảm bảo dữ liệu diagram đã có đầy đủ category_name
  const processedData = prepareDiagramData(data);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Hàm đệ quy để xử lý node và con của nó
  const processNode = (
    node: DiagramData,
    level: number = 0,
    position: number = 0,
    parentId: string | null = null,
  ) => {
    // Tính toán vị trí dựa trên cấp độ và vị trí
    const y = position * 250;
    const x = level * 450;

    // Thêm node vào danh sách
    nodes.push({
      id: node.id,
      type: "diagramNode",
      data: {
        label: node.name,
        description: node.description,
        image: node.image,
        showImage: node.showImage,
        type: node.id.split("-").pop() || "",
        isOval: true,
        // Sử dụng category_name nếu có, nếu không thì sử dụng category_id
        category: node.category_id,
        category_name: node.category_name,
      },
      position: { x, y },
    });

    // Thêm edge nếu có parent
    if (parentId) {
      // Xác định màu sắc: highlight (đỏ) hoặc bình thường (xám)
      const isHighlighted = node.highlight === true;
      const edgeColor = isHighlighted ? "#ef4444" : "#64748b";

      edges.push({
        id: createEdgeId(parentId, node.id),
        source: parentId,
        target: node.id,
        type: "default",
        animated: false,
        style: {
          strokeWidth: 2,
          stroke: edgeColor,
        },
        markerEnd:
          level > 1
            ? {
                type: MarkerType.Arrow,
                width: 20,
                height: 20,
                color: edgeColor,
              }
            : undefined,
      });
    }

    // Xử lý các node con
    if (node.children && node.children.length > 0) {
      // Tính toán vị trí bắt đầu cho các node con
      const childrenCount = node.children.length;
      const startPosition = position - (childrenCount - 1) / 2;

      node.children.forEach((child, index) => {
        const childPosition = startPosition + index;
        processNode(child, level + 1, childPosition, node.id);
      });
    }
  };

  // Bắt đầu từ node gốc
  processNode(processedData);

  return { nodes, edges };
};

// Chuyển đổi dữ liệu sang định dạng phức tạp đa cấp (Complex Multi-tier)
export const transformToComplexMultiTier = (
  data: DiagramData,
): { nodes: Node[]; edges: Edge[] } => {
  // Đảm bảo dữ liệu diagram đã có đầy đủ category_name
  const processedData = prepareDiagramData(data);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Phân tích độ sâu của cây để có chiến lược bố trí phù hợp
  const analyzeTreeDepth = (node: DiagramData): number => {
    if (!node.children || node.children.length === 0) {
      return 0;
    }

    const childDepths = node.children.map((child) => analyzeTreeDepth(child));
    return 1 + Math.max(...childDepths);
  };

  // Đếm tổng số node trong cây
  const countNodes = (node: DiagramData): number => {
    let count = 1; // Tính cả node hiện tại
    if (node.children) {
      node.children.forEach((child) => {
        count += countNodes(child);
      });
    }
    return count;
  };

  const treeDepth = analyzeTreeDepth(processedData);
  const totalNodes = countNodes(processedData);

  // Thông số bố cục dựa trên độ phức tạp
  const levelHeight = 150; // Khoảng cách giữa các cấp
  const nodeWidth = 200; // Chiều rộng ngang giữa các node cùng cấp
  const isComplex = treeDepth > 3 || totalNodes > 50;

  // Lưu trữ các kết nối chéo giữa các node (nếu có)
  const crossConnections: { source: string; target: string; label?: string }[] =
    [];

  // Hàm đệ quy để xử lý node và con của nó với bố cục phân cấp thông minh
  const processNode = (
    node: DiagramData,
    level: number = 0,
    x: number = 0,
    parentPosition: { x: number; y: number } | null = null,
    parentId: string | null = null,
    siblingIndex: number = 0,
    totalSiblings: number = 1,
    branchWidth: number = 1200,
  ) => {
    // Phân tích số lượng node con ở mỗi nhánh để tối ưu bố cục
    const childCount = node.children ? node.children.length : 0;

    // Độ rộng của nhánh hiện tại
    const currentWidth = childCount > 0 ? branchWidth : nodeWidth;

    // Tính toán vị trí Y dựa trên cấp độ
    const y = level * levelHeight;

    // Cho cấp 0 (root), đặt ở giữa màn hình
    const currentX = level === 0 ? 0 : x;

    // Xác định màu sắc dựa vào category hoặc cấp độ
    const getNodeColor = (node: DiagramData, level: number): string => {
      const defaultColors = [
        "#4a90e2",
        "#50e3c2",
        "#b8e986",
        "#9013fe",
        "#f5a623",
        "#f8e71c",
        "#bd10e0",
        "#8b572a",
        "#7ed321",
        "#417505",
      ];

      // Ưu tiên sử dụng category_id nếu có
      if (node.category_id) {
        // Đơn giản hóa: sử dụng hash của category_id để chọn màu
        const hash = node.category_id
          .split("")
          .reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return defaultColors[hash % defaultColors.length];
      }

      // Nếu không có category, sử dụng cấp độ
      return defaultColors[level % defaultColors.length];
    };

    // Định dạng node dựa vào cấp độ và loại
    const nodeStyle = (node: DiagramData, level: number) => {
      // Cấp 0: Root node lớn nhất, hình chữ nhật
      if (level === 0) {
        return {
          width: 250,
          height: 80,
          borderRadius: 8,
          fontSize: 18,
          fontWeight: "bold" as const,
          backgroundColor: getNodeColor(node, level),
          color: "white",
        };
      }

      // Cấp 1: Node lớn, màu đậm
      if (level === 1) {
        return {
          width: 200,
          height: 70,
          borderRadius: 6,
          fontSize: 16,
          fontWeight: "bold" as const,
          backgroundColor: getNodeColor(node, level),
          opacity: 0.9,
          color: "white",
        };
      }

      // Cấp 2-3: Node trung bình
      if (level <= 3) {
        return {
          width: 180,
          height: 60,
          borderRadius: 5,
          fontSize: 14,
          backgroundColor: getNodeColor(node, level),
          opacity: 0.8,
          color: "white",
        };
      }

      // Cấp 4+: Node nhỏ
      return {
        width: 160,
        height: 50,
        borderRadius: 4,
        fontSize: 12,
        backgroundColor: getNodeColor(node, level),
        opacity: 0.7,
        color: "white",
      };
    };

    // Thêm node vào danh sách với style tương ứng
    const style = nodeStyle(node, level);

    nodes.push({
      id: node.id,
      type: "diagramNode",
      data: {
        label: node.name,
        description: node.description,
        image: node.image,
        showImage: node.showImage,
        type: node.type || node.id.split("-").pop() || "",
        isOval: level > 3, // Cấp cao hơn 3 dùng hình oval
        category: node.category_id,
        category_name: node.category_name,
        level: level, // Lưu trữ cấp độ để dễ truy vấn
        style: style,
      },
      position: { x: currentX, y },
      style: {
        width: style.width,
        height: style.height,
        borderRadius: style.borderRadius,
        fontSize: `${style.fontSize}px`,
        fontWeight: style.fontWeight,
        backgroundColor: style.backgroundColor,
        opacity: style.opacity,
        color: style.color,
      },
    });

    // Thêm edge nếu có parent
    if (parentId && parentPosition) {
      // Xác định kiểu đường kết nối dựa vào cấp độ
      const edgeType = level <= 2 ? "smoothstep" : "default";

      // Xác định màu sắc: highlight (đỏ) hoặc màu tương ứng
      const isHighlighted = node.highlight === true;
      const edgeColor = isHighlighted
        ? "#ef4444"
        : level === 1
          ? "#333333"
          : "#64748b";

      // Độ dày đường giảm dần theo cấp
      const strokeWidth = Math.max(3 - level * 0.5, 1);

      edges.push({
        id: createEdgeId(parentId, node.id),
        source: parentId,
        target: node.id,
        type: edgeType,
        animated: false,
        label: node.edgeLabel,
        labelStyle: { fill: "#888", fontWeight: 500, fontSize: 12 },
        style: {
          strokeWidth: strokeWidth,
          stroke: edgeColor,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 15,
          height: 15,
          color: edgeColor,
        },
      });
    }

    // Lưu trữ các kết nối chéo nếu node có định nghĩa connections
    if (node.connections) {
      node.connections.forEach((conn) => {
        crossConnections.push({
          source: node.id,
          target: conn.target,
          label: conn.label,
        });
      });
    }

    // Xử lý các node con
    if (node.children && node.children.length > 0) {
      // Tính toán chiều rộng khả dụng cho mỗi nhánh con
      const childBranchWidth = currentWidth / childCount;

      // Phân bố các node con đều trên trục X
      node.children.forEach((child, idx) => {
        // Tính toán vị trí X cho mỗi node con
        const childOffset = (idx - (childCount - 1) / 2) * childBranchWidth;
        const childX = currentX + childOffset;

        processNode(
          child,
          level + 1,
          childX,
          { x: currentX, y },
          node.id,
          idx,
          childCount,
          childBranchWidth,
        );
      });
    }
  };

  // Bắt đầu từ node gốc
  processNode(processedData);

  // Xử lý các kết nối chéo (cross-connections)
  crossConnections.forEach((conn, idx) => {
    edges.push({
      id: `cross-${conn.source}-${conn.target}-${idx}`,
      source: conn.source,
      target: conn.target,
      type: "straight",
      animated: true,
      label: conn.label,
      labelStyle: { fill: "#888", fontWeight: 500, fontSize: 12 },
      style: {
        strokeWidth: 1,
        stroke: "#FF5722",
        strokeDasharray: "5,5",
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 12,
        height: 12,
        color: "#FF5722",
      },
    });
  });

  return { nodes, edges };
};
